
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import { GobiColor } from '@/helpers/color';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		StackedBarChartFilterable,
		IncludeTaxToggle,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesByAgentsBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales'];
	}

	public filterIds: Array<
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange', 'stockItems', 'agents'];

	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			includeTax,
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			includeTax,
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['sales', 'creditNotes', 'customerTrades'] as any[],
		);
		this.checkInterrupted(jobId);

		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');

		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			&& this.selectedAgents.length !== allAgentLen
			? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';
		const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
		const stockItemFilter = this.selectedStockItems && this.selectedStockItems.length > 0
			&& this.selectedStockItems.length !== allStockItemsLen
			? ` AND ${COL.ITEMCODE} IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
		const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;
		const salesFilter = `${dateFilter}${agentFilter}${stockItemFilter}${docTypeFilter}`;
		const cnFilter = `${dateFilter}${agentFilter}${stockItemFilter}`;
		const arFilter = `${dateFilter}${agentFilter}`;

		const amtExpr = this.includeTax
			? '(TRY_CAST("AMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("TAX" AS DOUBLE), 0))'
			: 'TRY_CAST("AMOUNT" AS DOUBLE)';

		// Step 1: Get top 6 stock groups by total amount
		const sgRows = await queryHead.rawQuery(`
			SELECT "STOCKGROUP" as sg, SUM(${amtExpr}) as totalAmt
			FROM ${salesTable}
			WHERE ${salesFilter}
			GROUP BY "STOCKGROUP"
			ORDER BY totalAmt DESC
			LIMIT 6
		`);

		const stockGroups = sgRows.map((r: any) => String(r.sg));
		const sgNames = gdbx.stockGroupNames;
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.9);

		// Step 2: Build dynamic CASE WHEN for each stock group
		const sgCases = stockGroups.map((sg: string, i: number) => {
			const escaped = sg.replace(/'/g, '\'\'');
			return `SUM(CASE WHEN "STOCKGROUP" = '${escaped}' THEN ${amtExpr} ELSE 0 END) as sg_${i}`;
		}).join(',\n\t\t\t');

		// Step 3: Query sales by agent with stock group breakdown
		const salesByAgent = await queryHead.rawQuery(`
			SELECT "AGENT" as agent,
				${sgCases},
				SUM(${amtExpr}) as totalAmt
			FROM ${salesTable}
			WHERE ${salesFilter}
			GROUP BY "AGENT"
			ORDER BY totalAmt DESC
		`);

		// Step 4: Query CN by agent with same stock group breakdown (if needed)
		type AgentSGRow = any;
		let cnByAgent: AgentSGRow[] = [];
		if (this.includeCreditNote) {
			cnByAgent = await queryHead.rawQuery(`
				SELECT "AGENT" as agent,
					${sgCases},
					SUM(${amtExpr}) as totalAmt
				FROM ${cnTable}
				WHERE ${cnFilter}
				GROUP BY "AGENT"
			`);
		}

		const cnMap = new Map<string, AgentSGRow>();
		for (const r of cnByAgent) { cnMap.set(String(r.agent), r); }

		// Step 5: AR mode - query customerTrades by agent
		const arDocumentByAgent: { [key: string]: number } = {};
		if (!this.slPhModuleOnly) {
			const arByAgent = await queryHead.rawQuery(`
				SELECT "AGENT" as agent,
					SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as ivAmt,
					SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as dnAmt,
					SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as cnAmt
				FROM ${arTable}
				WHERE ${arFilter}
				GROUP BY "AGENT"
			`);

			for (const ar of arByAgent) {
				const agent = String(ar.agent);
				const arTotal = (Number(ar.ivAmt) || 0)
					+ (this.includeDebitNote ? (Number(ar.dnAmt) || 0) : 0)
					- (this.includeCreditNote ? (Number(ar.cnAmt) || 0) : 0);

				// Find SL total for this agent
				const slRow = salesByAgent.find((s: any) => String(s.agent) === agent);
				const cnRow = cnMap.get(agent);
				const slTotal = (Number(slRow && slRow.totalAmt) || 0)
					- (this.includeCreditNote ? (Number(cnRow && cnRow.totalAmt) || 0) : 0);

				const arDocument = arTotal - slTotal;
				if (arDocument !== 0) {
					arDocumentByAgent[agent] = arDocument;
				}
			}
		}

		// Step 6: Build results — apply CN subtraction, limit to top agents
		interface AgentResult {
			agent: string;
			text: string;
			total: number;
			sgAmounts: number[];
		}

		let agentResults: AgentResult[] = salesByAgent.map((r: any) => {
			const agent = String(r.agent);
			const cn = cnMap.get(agent);
			const sgAmounts = stockGroups.map((sg: string, i: number) => {
				const salesAmt = Number(r['sg_' + i]) || 0;
				const cnAmt = this.includeCreditNote ? (Number(cn && cn['sg_' + i]) || 0) : 0;
				return salesAmt - cnAmt;
			});
			const salesTotal = Number(r.totalAmt) || 0;
			const cnTotal = this.includeCreditNote ? (Number(cn && cn.totalAmt) || 0) : 0;
			return {
				agent,
				text: agent,
				total: salesTotal - cnTotal,
				sgAmounts,
			};
		});

		// Filter zero results (unless AR has a document for them)
		agentResults = agentResults.filter((dd) => dd.total !== 0 || arDocumentByAgent[dd.agent]);

		// Limit to top slices
		if (agentResults.length > 10) {
			agentResults = agentResults.slice(0, 10);
		}
		this.generateLoadingText(0.95);

		this.xAxisData = agentResults.map((dd) => dd.text);

		// Build series for each stock group
		this.series = stockGroups.map((sg: string, i: number) => ({
			name: sgNames[sg] || sg,
			data: agentResults.map((dd) => dd.sgAmounts[i]),
			type: 'bar',
			barWidth: '40%',
			stack: 'a',
		}));

		// Add AR Document series if in AR mode
		if (!this.slPhModuleOnly) {
			this.series.push({
				name: 'Customer Module Documents',
				data: agentResults.map((dd) => arDocumentByAgent[dd.agent] || 0),
				type: 'bar',
				barWidth: '40%',
				stack: 'a',
				color: GobiColor.COLOR6,
			});
		}

		// Total bar
		this.series.push({
			name: 'Total',
			type: 'bar',
			barWidth: '10%',
			color: GobiColor.COLOR7,
			data: agentResults.map((dd) => dd.total + (arDocumentByAgent[dd.agent] || 0)),
		});

		this.saveHistory(
			'series',
			'xAxisData',
			'periodTotalData',
			'periodTotalFields',
		);
	}
}
